import React from "react";
import { InputNumber, InputNumberValueChangeEvent } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { Col, FormGroup, Label, Row } from "reactstrap";
import { AppCard } from "../../components/ui";
import { X, Check } from "lucide-react";
import { riskApiService } from "../../services/RiskService";
import { AMLPrecisionData } from "../../models/AMLPrecisionData";
import { toastService } from "../../services/toastService";


// --- INTERFACES ---

interface AMLPrecisionProps {
    onHide: () => void;
}

interface AMLPrecisionState {
    id: number | null;

    // Onboarding Precision
    onboardingMinimumMatchingPercentageNIL: number | null;
    onboardingMinimumMatchingPercentagePEP: number | null;

    // Transactions Precision
    transactionsMinimumMatchingPercentageNIL: number | null;
    transactionsMinimumMatchingPercentagePEP: number | null;

    // Monitoring Precision
    monitoringMinimumMatchingPercentageNIL: number | null;
    monitoringMinimumMatchingPercentagePEP: number | null;

    isLoading: boolean;
    hasChanges: boolean;
}

// --- COMPONENT ---

class AMLPrecisionForm extends React.Component<AMLPrecisionProps, AMLPrecisionState> {
    constructor(props: AMLPrecisionProps | Readonly<AMLPrecisionProps>) {
        super(props);
        this.state = {
            id: null,
            onboardingMinimumMatchingPercentageNIL: null,
            onboardingMinimumMatchingPercentagePEP: null,
            transactionsMinimumMatchingPercentageNIL: null,
            transactionsMinimumMatchingPercentagePEP: null,
            monitoringMinimumMatchingPercentageNIL: null,
            monitoringMinimumMatchingPercentagePEP: null,
            isLoading: false,
            hasChanges: false,
        };
    }

    async componentDidMount() {
        await this.loadData();
    }

    // --- UTILITY FUNCTIONS ---
    private transformInitialData = (value: number | null) => {
        return (value === 0 || value === null) ? null : value;
    };

    private transformForSave = (val: number | null): number => {
        if (val === null) return 0; // empty -> send 0
        if (val === 0) return 1;   // 0 -> send 1
        return val;
    };

    // --- MAIN FUNCTIONS ---
    loadData = async () => {
        this.setState({ isLoading: true });

        try {
            const result = await riskApiService.getAMLPrecisionData();

            if (result.success && result.data) {
                this.setState({
                    id: result.data.id,
                    onboardingMinimumMatchingPercentageNIL: this.transformInitialData(result.data.onboardingMinimumMatchingPercentageNIL),
                    onboardingMinimumMatchingPercentagePEP: this.transformInitialData(result.data.onboardingMinimumMatchingPercentagePEP),
                    transactionsMinimumMatchingPercentageNIL: this.transformInitialData(result.data.transactionsMinimumMatchingPercentageNIL),
                    transactionsMinimumMatchingPercentagePEP: this.transformInitialData(result.data.transactionsMinimumMatchingPercentagePEP),
                    monitoringMinimumMatchingPercentageNIL: this.transformInitialData(result.data.monitoringMinimumMatchingPercentageNIL),
                    monitoringMinimumMatchingPercentagePEP: this.transformInitialData(result.data.monitoringMinimumMatchingPercentagePEP),
                    hasChanges: false,
                });
            } else {
                toastService.error(result.message || "Failed to load AML precision data");
            }
        } catch {
            toastService.error("Failed to load AML precision data");
        } finally {
            this.setState({ isLoading: false });
        }
    };

    onFieldUpdate = (fieldName: keyof AMLPrecisionState, value: number | null) => {
        this.setState({
            [fieldName]: value,
            hasChanges: true,
        } as Pick<AMLPrecisionState, keyof AMLPrecisionState>);
    };

    handleSave = async () => {
        this.setState({ isLoading: true });
        
        try {
            const data: AMLPrecisionData = {
                id: this.state.id,

                onboardingMinimumMatchingPercentageNIL: this.transformForSave(this.state.onboardingMinimumMatchingPercentageNIL),
                onboardingMinimumMatchingPercentagePEP: this.transformForSave(this.state.onboardingMinimumMatchingPercentagePEP),
        
                transactionsMinimumMatchingPercentageNIL: this.transformForSave(this.state.transactionsMinimumMatchingPercentageNIL),
                transactionsMinimumMatchingPercentagePEP: this.transformForSave(this.state.transactionsMinimumMatchingPercentagePEP),
        
                monitoringMinimumMatchingPercentageNIL: this.transformForSave(this.state.monitoringMinimumMatchingPercentageNIL),
                monitoringMinimumMatchingPercentagePEP: this.transformForSave(this.state.monitoringMinimumMatchingPercentagePEP),
            };

            const result = await riskApiService.updateAMLPrecisionData(data);

            if (result.success) {
                toastService.success("AML precision data saved successfully");
                this.setState({ hasChanges: false });
            } else {
                toastService.error(result.message || "Failed to save AML precision data");
            }
        } catch {
            toastService.error("Failed to save AML precision data");
        } finally {
            this.setState({ isLoading: false });
        }
    };

    handleCancel = () => {
        this.loadData(); // Reload original data to discard changes
    };

    renderPrecisionSection = (
        title: string,
        nilFieldName: keyof AMLPrecisionState,
        pepFieldName: keyof AMLPrecisionState
    ) => {
        const nilValue = this.state[nilFieldName] as number | null;
        const pepValue = this.state[pepFieldName] as number | null;

        return (
            <React.Fragment>
                <h6 className="mb-3" style={{ fontWeight: "bold", color: "#495057" }}>{title}</h6>
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label htmlFor={nilFieldName}>Minimum Matching Percentage NIL</Label>
                            <small className="form-text text-muted">0-100%</small>
                            <InputNumber
                                value={nilValue}
                                inputId={nilFieldName}
                                className="w-100"
                                inputClassName="form-control placeholder:text-black/20"
                                placeholder="Enter percentage or leave empty"
                                onValueChange={(e: InputNumberValueChangeEvent) => this.onFieldUpdate(nilFieldName, e.value ?? null)}
                                disabled={this.state.isLoading}
                                min={0}
                                max={100}
                                step={1}
                                showButtons
                            />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label htmlFor={pepFieldName}>Minimum Matching Percentage PEP</Label>
                            <small className="form-text text-muted">0-100%</small>
                            <InputNumber
                                value={pepValue}
                                inputId={pepFieldName}
                                className="w-100"
                                inputClassName="form-control placeholder:text-black/20"
                                placeholder="Enter percentage or leave empty"
                                onValueChange={(e: InputNumberValueChangeEvent) => this.onFieldUpdate(pepFieldName, e.value ?? null)}
                                disabled={this.state.isLoading}
                                min={0}
                                max={100}
                                step={1}
                                showButtons
                            />
                        </FormGroup>
                    </Col>
                </Row>
            </React.Fragment>
        );
    };

    render() {
        return (
            <React.Fragment>
                <AppCard className="!mt-0 mb-3">
                    <AppCard.Header title="AML Precision Settings" />

                    <AppCard.Body>
                        <div>
                            {this.renderPrecisionSection(
                                "Onboarding Precision",
                                "onboardingMinimumMatchingPercentageNIL",
                                "onboardingMinimumMatchingPercentagePEP"
                            )}

                            <hr className="my-4" />

                            {this.renderPrecisionSection(
                                "Transactions Precision",
                                "transactionsMinimumMatchingPercentageNIL",
                                "transactionsMinimumMatchingPercentagePEP"
                            )}

                            <hr className="my-4" />

                            {this.renderPrecisionSection(
                                "Monitoring Precision",
                                "monitoringMinimumMatchingPercentageNIL",
                                "monitoringMinimumMatchingPercentagePEP"
                            )}

                            <div className="d-flex justify-content-end mt-4">
                                <Button
                                    label="Cancel"
                                    icon={<X size={16} />}
                                    severity="secondary"
                                    onClick={this.handleCancel}
                                    disabled={this.state.isLoading || !this.state.hasChanges}
                                    style={{ marginRight: "10px" }}
                                />
                                <Button
                                    label="Save"
                                    icon={<Check size={16} />}
                                    severity="success"
                                    onClick={this.handleSave}
                                    loading={this.state.isLoading}
                                    disabled={!this.state.hasChanges}
                                />
                            </div>
                        </div>
                    </AppCard.Body>
                </AppCard>
            </React.Fragment>
        );
    }
}

export default AMLPrecisionForm;